import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import apiClient from '../api/client';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Helper function to fetch user profile from backend
const fetchUserProfile = async () => {
    try {
        const response = await apiClient.get('/me');
        return response.data;
    } catch (error) {
        console.error('Failed to fetch user profile:', error);
        return null;
    }
};

const migrateLocalPortfoliosIfNeeded = async () => {
    // Only migrate if localStorage has data and server has none.
    let local = null;
    try {
        const raw = localStorage.getItem('portfolioData');
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) {
                local = parsed;
            }
        }
    } catch {
        local = null;
    }
    if (!local) return;

    try {
        const res = await apiClient.get('/portfolios');
        const serverPortfolios = res?.data;
        if (Array.isArray(serverPortfolios) && serverPortfolios.length > 0) {
            return; // server already has data; do not overwrite
        }
    } catch (e) {
        // If auth is not ready (401), do not attempt migration and do not touch local data.
        if (e?.response?.status === 401) return;
        // If server check fails for any other reason, do not attempt migration.
        return;
    }

    try {
        await apiClient.put('/portfolios', { portfolios: local, only_if_empty: true });
        // keep silent in production
    } catch (e) {
        console.warn('Failed to migrate local portfolios:', e?.response?.data || e?.message || e);
    }
};

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(Cookies.get('access_token'));
    const [idToken, setIdToken] = useState(Cookies.get('id_token'));
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const refreshInFlightRef = useRef(false);
    const refreshTimerRef = useRef(null);
    const logoutOnTabClose = String(process.env.REACT_APP_AUTH_LOGOUT_ON_TAB_CLOSE || '').toLowerCase() === 'true';

    const clearAuthStorage = () => {
        Cookies.remove('access_token');
        Cookies.remove('id_token');
        Cookies.remove('refresh_token');
        Cookies.remove('auth_username');
        try {
            localStorage.removeItem('access_token');
        } catch {
            // ignore storage failures (private mode, quotas, tests)
        }
    };

    const buildCookieOptions = (expires) => {
        const isHttps = (() => {
            try {
                return typeof window !== 'undefined' && window.location?.protocol === 'https:';
            } catch {
                return false;
            }
        })();
        const opts = {
            expires,
            secure: isHttps,
            // Lax is safer for top-level navigations/new tabs than Strict,
            // while still protecting against most CSRF contexts.
            sameSite: 'lax',
        };
        const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;
        if (cookieDomain) {
            opts.domain = cookieDomain;
        }
        return opts;
    };

    const decodeTokenExp = (jwtToken) => {
        try {
            if (!jwtToken) return null;
            const decoded = jwtDecode(jwtToken);
            if (!decoded?.exp) return null;
            return new Date(decoded.exp * 1000);
        } catch {
            return null;
        }
    };

    const getTokenSecondsRemaining = (jwtToken) => {
        try {
            if (!jwtToken) return 0;
            const decoded = jwtDecode(jwtToken);
            if (!decoded?.exp) return 0;
            return Math.max(0, decoded.exp - (Date.now() / 1000));
        } catch {
            return 0;
        }
    };

    const setAccessTokenCookie = (accessToken) => {
        const accessExp = decodeTokenExp(accessToken);
        Cookies.set('access_token', accessToken, buildCookieOptions(accessExp || undefined));
        setToken(accessToken);
        try {
            localStorage.setItem('access_token', accessToken);
        } catch {
            // ignore storage failures (private mode, quotas, tests)
        }
        try {
            apiClient.defaults = apiClient.defaults || {};
            apiClient.defaults.headers = apiClient.defaults.headers || {};
            apiClient.defaults.headers.common = apiClient.defaults.headers.common || {};
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        } catch {
            // no-op in tests/mocked clients
        }
    };

    const setIdTokenCookie = (nextIdToken) => {
        if (!nextIdToken) return;
        const nextIdExp = decodeTokenExp(nextIdToken);
        Cookies.set('id_token', nextIdToken, buildCookieOptions(nextIdExp || undefined));
        setIdToken(nextIdToken);
    };

    const refreshSession = async () => {
        if (refreshInFlightRef.current) return false;
        const refreshToken = Cookies.get('refresh_token');
        const username = Cookies.get('auth_username');
        if (!refreshToken || !username) return false;

        refreshInFlightRef.current = true;
        try {
            const response = await apiClient.post('/auth/refresh', {
                refresh_token: refreshToken,
                username,
            });
            const nextAccess = response?.data?.access_token;
            const nextId = response?.data?.id_token;
            if (!nextAccess) return false;

            setAccessTokenCookie(nextAccess);
            // Cognito may not always return id_token for refresh flow.
            if (nextId) setIdTokenCookie(nextId);

            setIsAuthenticated(true);
            await bootstrapProfile(nextId || Cookies.get('id_token'));
            const profile = await fetchUserProfile();
            let decoded = null;
            try { decoded = jwtDecode(nextAccess); } catch {}
            if (profile) {
                setUser({ ...(decoded || {}), username: profile.username || profile.email || 'User' });
            } else {
                setUser({ ...(decoded || {}), username: (decoded && decoded.email) || username || 'User' });
            }
            return true;
        } catch (error) {
            console.warn('Session refresh failed:', error?.response?.data || error?.message || error);
            return false;
        } finally {
            refreshInFlightRef.current = false;
        }
    };

    const bootstrapProfile = async (idTokenValue) => {
        if (!idTokenValue) return;
        try {
            await apiClient.post('/profile/bootstrap', null, {
                headers: { Authorization: `Bearer ${idTokenValue}` },
            });
        } catch (error) {
            // Non-fatal: /me may still work, or profile can be provisioned later.
            console.warn('Profile bootstrap failed:', error?.response?.data || error?.message || error);
        }
    };

    useEffect(() => {
        const initAuth = async () => {
            if (token) {
                try {
                    // Ensure API client is primed with the current token as early as possible.
                    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    const decoded = jwtDecode(token);
                    const currentTime = Date.now() / 1000;

                    if (decoded.exp > currentTime) {
                        try {
                            localStorage.setItem('access_token', token);
                        } catch {
                            // ignore storage failures (private mode, quotas, tests)
                        }
                        setIsAuthenticated(true);
                        // Best-effort: if we have an ID token, bootstrap profile attributes first.
                        await bootstrapProfile(Cookies.get('id_token'));
                        // Fetch user profile from backend to get username
                        const profile = await fetchUserProfile();
                        if (profile) {
                            setUser({ ...decoded, username: profile.username || profile.email || 'User' });
                        } else {
                            setUser({ ...decoded, username: decoded.email || 'User' });
                        }
                    } else {
                        // Token expired: try refresh-token flow first.
                        const refreshed = await refreshSession();
                        if (!refreshed) logout();
                    }
                } catch (error) {
                    console.error('Invalid token:', error);
                    const refreshed = await refreshSession();
                    if (!refreshed) logout();
                }
            } else {
                // New tab / fresh app load with no access token in memory:
                // try refresh-token flow to restore session.
                try {
                    localStorage.removeItem('access_token');
                } catch {
                    // ignore storage failures (private mode, quotas, tests)
                }
                await refreshSession();
            }
            setLoading(false);
        };
        initAuth();
    }, [token]);

    useEffect(() => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        if (!isAuthenticated || !token) return;

        let decoded;
        try {
            decoded = jwtDecode(token);
        } catch {
            return;
        }
        if (!decoded?.exp) return;

        // Refresh ~5 minutes before expiry (or immediately if already near expiry).
        const refreshLeadMs = 5 * 60 * 1000;
        const msUntilRefresh = (decoded.exp * 1000) - Date.now() - refreshLeadMs;
        const delay = Math.max(0, msUntilRefresh);

        refreshTimerRef.current = setTimeout(async () => {
            const ok = await refreshSession();
            if (ok) return;

            // Avoid logging users out on transient network/service failures while
            // the current token is still valid. Retry shortly; logout only once
            // the access token has fully expired and refresh still fails.
            const secondsRemaining = getTokenSecondsRemaining(Cookies.get('access_token'));
            if (secondsRemaining > 0) {
                const retryMs = Math.min(60 * 1000, Math.max(5000, (secondsRemaining * 1000) - 5000));
                refreshTimerRef.current = setTimeout(async () => {
                    const retryOk = await refreshSession();
                    if (!retryOk && getTokenSecondsRemaining(Cookies.get('access_token')) <= 0) {
                        logout();
                    }
                }, retryMs);
                return;
            }

            logout();
        }, delay);

        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [isAuthenticated, token]);

    useEffect(() => {
        if (!logoutOnTabClose) return;

        const onBeforeUnload = () => {
            clearAuthStorage();
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [logoutOnTabClose]);

    const login = async (newAccessToken, newIdToken, newRefreshToken, usernameHint) => {
        try {
            const decoded = jwtDecode(newAccessToken);
            setAccessTokenCookie(newAccessToken);

            if (newIdToken) {
                // Persist id_token for bootstrap calls.
                try {
                    setIdTokenCookie(newIdToken);
                } catch (e) {
                    console.warn('Failed to decode/store id_token:', e);
                }
            }

            if (newRefreshToken) {
                // Refresh token often has long validity; keep as device session anchor.
                Cookies.set('refresh_token', newRefreshToken, buildCookieOptions(365));
            }
            const authUsername = usernameHint || decoded?.email;
            if (authUsername) {
                Cookies.set('auth_username', authUsername, buildCookieOptions(365));
            }

            setIsAuthenticated(true);

            // Ensure subsequent API calls pick up the fresh access token.
            // (Cookies are synchronous, but this avoids edge cases if called immediately.)
            try {
                apiClient.defaults = apiClient.defaults || {};
                apiClient.defaults.headers = apiClient.defaults.headers || {};
                apiClient.defaults.headers.common = apiClient.defaults.headers.common || {};
                apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
            } catch {
                // no-op in tests/mocked clients
            }

            await bootstrapProfile(newIdToken || Cookies.get('id_token'));

            // Migrate local portfolioData into Mongo if server has none.
            await migrateLocalPortfoliosIfNeeded();
            
            // Fetch user profile from backend to get username
            const profile = await fetchUserProfile();
            if (profile) {
                setUser({ ...decoded, username: profile.username || profile.email || 'User' });
            } else {
                setUser({ ...decoded, username: decoded.email || 'User' });
            }
        } catch (error) {
            console.error('Failed to decode token on login:', error);
            logout();
        }
    };

    const logout = () => {
        clearAuthStorage();
        try {
            delete apiClient.defaults.headers.common['Authorization'];
        } catch {
            // ignore
        }
        setToken(null);
        setIdToken(null);
        setUser(null);
        setIsAuthenticated(false);
    };

    const value = { user, isAuthenticated, loading, login, logout, token };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

